import React, { useState, useEffect } from 'react';
import {
  Modal,
  Box,
  Typography,
  Button,
  Paper,
  IconButton,
  useTheme,
  alpha,
  Checkbox,
  FormControlLabel,
  Chip,
} from '@mui/material';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer,
  LineChart,
  Line,
  BarChart,
  Bar,
} from 'recharts';
import CloseIcon from '@mui/icons-material/Close';

const TrendModal = ({ 
  open, 
  onClose, 
  title, 
  data, 
  chartType = 'area', 
  dataKey, 
  yAxisLabel,
  color = null,
  showLegend = true,
  height = 400,
  metrics = [] // Array of {key, title, color}
}) => {
  const theme = useTheme();
  
  // State to track which metrics are enabled
  const [enabledMetrics, setEnabledMetrics] = useState(new Set());
  
  // Initialize all metrics as enabled when modal opens or metrics change
  useEffect(() => {
    if (metrics && metrics.length > 0) {
      setEnabledMetrics(new Set(metrics.map(m => m.key)));
    }
  }, [metrics, open]);
  
  const toggleMetric = (metricKey) => {
    setEnabledMetrics(prev => {
      const newSet = new Set(prev);
      if (newSet.has(metricKey)) {
        newSet.delete(metricKey);
      } else {
        newSet.add(metricKey);
      }
      return newSet;
    });
  };

  const selectAllMetrics = () => {
    if (metrics && metrics.length > 0) {
      setEnabledMetrics(new Set(metrics.map(m => m.key)));
    }
  };

  const deselectAllMetrics = () => {
    setEnabledMetrics(new Set());
  };
  
  const getChartColor = () => {
    if (color) return color;
    return theme.palette.primary.main;
  };

  // Format minutes to "XXhr YYmin" format
  const formatMinutesToHours = (minutes) => {
    if (!minutes || minutes === 0) return '0hr 0min';
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours}hr ${mins}min`;
  };

  // Custom tooltip content component
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <Paper
          sx={{
            p: 1.5,
            bgcolor: 'background.paper',
            border: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
            boxShadow: theme.shadows[4],
            borderRadius: 1,
          }}
        >
          <Typography
            variant="body2"
            sx={{
              fontWeight: 600,
              mb: 1,
              color: 'text.primary',
            }}
          >
            {label}
          </Typography>
          {payload.map((entry, index) => {
            const metric = metrics.find(m => m.key === entry.dataKey);
            let displayValue = entry.value;
            
            // Format total_talktime to show minutes with hours/minutes in brackets
            if (entry.dataKey === 'total_talktime') {
              const formattedTime = formatMinutesToHours(entry.value);
              displayValue = `${entry.value} (${formattedTime})`;
            }
            // Add % symbol for completion percentage metrics
            else if (entry.dataKey === 'followup_completion_percentage' || entry.dataKey === 'site_visit_completion_percentage') {
              displayValue = `${entry.value}%`;
            }
            
            return (
              <Typography
                key={index}
                variant="body2"
                sx={{
                  color: entry.color,
                  mb: 0.5,
                }}
              >
                {metric?.title || entry.dataKey}: {displayValue}
              </Typography>
            );
          })}
        </Paper>
      );
    }
    return null;
  };

  const renderChart = () => {
    // If metrics array is provided, render multi-line chart
    if (metrics && metrics.length > 0) {
      // Filter to only show enabled metrics
      const enabledMetricsList = metrics.filter(m => enabledMetrics.has(m.key));
      
      return (
        <LineChart data={data} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="date" />
          <YAxis />
          <RechartsTooltip content={<CustomTooltip />} />
          <Legend 
            wrapperStyle={{ paddingTop: '20px' }}
            content={({ payload }) => {
              return (
                <Box sx={{ mt: 2 }}>
                  <Box sx={{ 
                    display: 'flex', 
                    justifyContent: 'center',
                    gap: 1,
                    mb: 1.5
                  }}>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={selectAllMetrics}
                      sx={{ fontSize: '0.75rem', minWidth: 'auto', px: 1.5 }}
                    >
                      Select All
                    </Button>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={deselectAllMetrics}
                      sx={{ fontSize: '0.75rem', minWidth: 'auto', px: 1.5 }}
                    >
                      Deselect All
                    </Button>
                  </Box>
                  <Box sx={{ 
                    display: 'flex', 
                    flexWrap: 'wrap', 
                    gap: 1, 
                    justifyContent: 'center'
                  }}>
                    {metrics.map((metric) => {
                      const isEnabled = enabledMetrics.has(metric.key);
                      return (
                        <Chip
                          key={metric.key}
                          label={metric.title}
                          onClick={() => toggleMetric(metric.key)}
                          sx={{
                            cursor: 'pointer',
                            bgcolor: isEnabled ? alpha(metric.color, 0.1) : 'grey.100',
                            color: isEnabled ? metric.color : 'text.disabled',
                            border: `1px solid ${isEnabled ? metric.color : 'grey.300'}`,
                            opacity: isEnabled ? 1 : 0.5,
                            '&:hover': {
                              opacity: 1,
                              bgcolor: isEnabled ? alpha(metric.color, 0.2) : 'grey.200',
                            },
                            '& .MuiChip-label': {
                              fontWeight: isEnabled ? 600 : 400,
                            }
                          }}
                          icon={
                            <Box
                              sx={{
                                width: 12,
                                height: 12,
                                borderRadius: '50%',
                                bgcolor: metric.color,
                                opacity: isEnabled ? 1 : 0.3,
                              }}
                            />
                          }
                        />
                      );
                    })}
                  </Box>
                </Box>
              );
            }}
          />
          {enabledMetricsList.map((metric) => (
            <Line
              key={metric.key}
              type="monotone"
              dataKey={metric.key}
              name={metric.title}
              stroke={metric.color}
              strokeWidth={2}
              activeDot={{ r: 6 }}
              connectNulls={false}
            />
          ))}
        </LineChart>
      );
    }
    
    // Otherwise render single metric chart
    const chartColor = getChartColor();
    
    switch (chartType) {
      case 'line':
        return (
          <LineChart data={data} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="date" />
            <YAxis label={{ value: yAxisLabel, angle: -90, position: 'insideLeft' }} />
            <RechartsTooltip />
            {showLegend && <Legend />}
            <Line 
              type="monotone" 
              dataKey={dataKey} 
              stroke={chartColor}
              strokeWidth={2}
              activeDot={{ r: 8 }} 
            />
          </LineChart>
        );
      
      case 'bar':
        return (
          <BarChart data={data} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="date" />
            <YAxis label={{ value: yAxisLabel, angle: -90, position: 'insideLeft' }} />
            <RechartsTooltip />
            {showLegend && <Legend />}
            <Bar 
              dataKey={dataKey} 
              fill={chartColor}
              radius={[4, 4, 0, 0]}
            />
          </BarChart>
        );
      
      case 'area':
      default:
        return (
          <AreaChart data={data} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="date" />
            <YAxis label={{ value: yAxisLabel, angle: -90, position: 'insideLeft' }} />
            <RechartsTooltip />
            {showLegend && <Legend />}
            <Area 
              type="monotone" 
              dataKey={dataKey} 
              stroke={chartColor} 
              fill={alpha(chartColor, 0.2)}
              activeDot={{ r: 8 }} 
            />
          </AreaChart>
        );
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      aria-labelledby="trend-modal-title"
      aria-describedby="trend-modal-description"
    >
      <Box sx={{
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: { xs: '90%', sm: 600, md: 800 },
        maxHeight: '90vh',
        bgcolor: 'background.paper',
        borderRadius: 2,
        boxShadow: 24,
        overflow: 'hidden',
      }}>
        <Paper sx={{ p: 3, pb: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography id="trend-modal-title" variant="h6" component="h2" sx={{ fontWeight: 600 }}>
              {title || 'Trend Data'}
            </Typography>
            <IconButton 
              onClick={onClose} 
              size="small"
              sx={{ 
                bgcolor: 'grey.100',
                '&:hover': { bgcolor: 'grey.200' }
              }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
          
          <Box id="trend-modal-description" sx={{ mt: 2 }}>
            {data && data.length > 0 ? (
              <ResponsiveContainer width="100%" height={height}>
                {renderChart()}
              </ResponsiveContainer>
            ) : (
              <Box sx={{ 
                display: 'flex', 
                justifyContent: 'center', 
                alignItems: 'center', 
                height: height,
                color: 'text.secondary'
              }}>
                <Typography>No trend data available</Typography>
              </Box>
            )}
          </Box>
        </Paper>
      </Box>
    </Modal>
  );
};

export default TrendModal;
